/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import {
  AfterViewChecked,
  Component,
  Inject,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { WorkPanelBarItem } from './models/work-panel-bar-items';
import { first, takeUntil } from 'rxjs/operators';
import {
  AppSettingsService,
  AuthorizationService,
  ErrorHandlerService,
  FeatureToggleService,
  GlobalEventBusService,
  GroupContextService,
  hd,
  IDeviceModel,
  IGroupModel,
  IRcConfiguration,
  LicenceInfoService,
  LicenceStatus,
  LicenseDetailsStatus,
  TicketContextService,
  TicketService,
  UserInfoService,
} from '@nx-fe/services';
import { Toast } from 'app/shared/components/toast/toast.component';
import { GlobalEvents } from 'app/core/global-event-constants';
import { RemoteControlService } from 'app/modules/remote-control/services/remote-control.service';
import { RemoteControlDeviceState, DeviceStateRegistryService } from 'app/modules/remote-control/services/device-state-registry.service';
import {
  WorkPanelManagerService,
  WorkPanelStatus,
  WorkPanelType,
} from 'app/modules/remote-control/services/work-panel-manager.service';
import { WorkPanelService } from 'app/modules/remote-control/services/work-panel.service';
import { AttachmentService } from '../../services/attachment.service';
import { SnapshotService } from '../../services/snapshot.service';
import { ISnapshotInformation } from '../snapshot/snapshot/snapshot.models';
import { Router } from '@angular/router';
import { Activity } from 'xsmodules/ticket/ticket.model';
import { IWorkPanelInformation } from './work-panel-rc2.models';
@Component({
  selector: 'app-work-panel-rc2',
  templateUrl: './work-panel-rc2.component.html',
  styleUrls: ['./work-panel-rc2.component.scss'],
})
export class WorkPanelRC2Component implements OnInit, OnDestroy, AfterViewChecked {
  @Input() popOut = false;
  @Input() standalone = false;
  @Input() canAddAttachment = false;
  @Input() canEmbedIntoTicket = true;
  @Output() openWorkPanelEvent = new EventEmitter<void>();
  @Output() closeWorkPanelEvent = new EventEmitter<void>();
  @ViewChild('workPanelTitle') workPanelTitleRef: ElementRef;

  /** Whether the licence is active */
  public isLicenseActive?: boolean | null = null;
  public menuBarItems: WorkPanelBarItem[];
  public titleHeight: number;
  public titleBarRef: TemplateRef<any>;
  public actionBarRef: TemplateRef<any>;
  public toast: Toast = null;

  public workPanelType = WorkPanelType; // only used to access this enum from template

  /** this key should be only used within the component that created it and its child components
   *  in this case it will be work panel component, remote-control-view, snapshot and file preview
   */
  public workPanelKey: string; // this presents the embedded panel

  public minimizedWorkPanelKey: string;

  private isPopInOutExternallyDisabled = false;

  /** Show group selection dialog for ticket creation */
  public showTicketCreate: boolean = false;

  public snapshotRefreshInProgress = false;

  private previewSnapshotSub: Subscription;
  private rcMaximizedSub: Subscription;
  private openLiveSnapshotSub: Subscription;
  private openRcWorkPanelSub: Subscription;
  private remoteControlMaximizedSub: Subscription;
  private remoteControlMinimizedSub: Subscription;
  private showToastMessageSub: Subscription;
  private popOutStartedSub: Subscription;
  private popInStartedSub: Subscription;
  private externalDisableSubscription: Subscription;
  private remoteControlCloseInitiatedSub: Subscription;
  private attachmentPreviewUnbind: Subscription;

  private rcMenuBarItems: WorkPanelBarItem[];

  private snapshotMenuBarItems: WorkPanelBarItem[];
  selectedGroupId: string;
  groups: IGroupModel[] = [];

  // minimum menu BarItem count when menu is not collapsible.
  private maxMenuBarItemNotCollapsible = 2;

  private remoteControlMinimizedUnbind: Function;
  private _alreadyAssociating: boolean;

  private unsubscribe$ = new Subject<void>();
  private errorMessage = '';
  private _isInVisualSelectionMode = false;

  constructor(
    private globalEventBusService: GlobalEventBusService,
    private workPanelManagerService: WorkPanelManagerService,
    private attachmentService: AttachmentService,
    private errorHandlerService: ErrorHandlerService,
    private appSettingsService: AppSettingsService,
    private ticketService: TicketService,
    private $uiRouter: Router,
    public licenseInfoService: LicenceInfoService,
    public remoteControlService: RemoteControlService,
    public deviceStateRegistry: DeviceStateRegistryService,
    public featureToggleService: FeatureToggleService,
    public ticketContextService: TicketContextService,
    public snapshotService: SnapshotService,
    public workPanelService: WorkPanelService,
    public authorizationService: AuthorizationService,
    private groupContextService: GroupContextService,
    private userInfoService: UserInfoService
  ) {
    this.externalDisableSubscription = this.remoteControlService.externalPopoutDisabled$.subscribe(isDisabled => {
      this.isPopInOutExternallyDisabled = isDisabled;
      // rootScope.$evalAsync();
    });
  }

  ngAfterViewChecked() {
    if (this.isSnapshotVisible()) {
      this.titleHeight = this.workPanelTitleRef.nativeElement.offsetHeight;
    }
  }

  private setLicenseActive(connectionId?: number): void {
    if (this.isLicenseActive !== null) {
      return;
    }
    this.licenseInfoService
      .getLicenseStatus$(connectionId)
      .pipe(first())
      .subscribe(licenseStatus => {
        this.isLicenseActive = licenseStatus === LicenseDetailsStatus.active;
        if (this.isLicenseActive && this.standalone && this.authorizationService.hasAnyRole()) {
          const deviceState = this.getCurrentRcState();
          if (deviceState && deviceState.connected) {
            this.associateWithTicketIfNeeded();
          } else {
            this.remoteControlService.remoteControlStarted$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
              this.associateWithTicketIfNeeded();
            });
          }
        }
      });
  }

  ngOnInit() {
    if (this.popOut) {
      this.isLicenseActive = true;
    }
    this.attachmentPreviewUnbind = this.globalEventBusService.register(
      GlobalEvents.previewAttachment,
      (activity: Activity) => {
        console.log('work panel - preview attachment:', activity);
        if (this.isRcOrLiveSnapshotVisible() && this.isRcConnectedOrHasError()) {
          this.transferEmbeddedPanelToMinimized();
        }
        if (activity != null) {
          this.addWorkPanel(WorkPanelType.filePreview);
        }
      }
    );

    this.previewSnapshotSub = this.globalEventBusService.register(
      GlobalEvents.previewSnapshot,
      (result: ISnapshotInformation) => {
        console.log('work panel - preview snapshot:', result);
        if (
          this.isRcOrLiveSnapshotVisible() &&
          this.isRcConnectedOrHasError() &&
          !this.workPanelManagerService.isPanelPoppedOut(this.workPanelKey)
        ) {
          this.transferEmbeddedPanelToMinimized();
        }
        const device = result ? result.device : null;
        // Preview device snapshot should not close both RC and live snapshot pop out (XS-10113)
        this.addWorkPanel(WorkPanelType.savedSnapshot, device, null, false);
        // HD-11194 temporary fix only for 1.8, but this might need to stay, further investigation needed
        // start
        const state = this.deviceStateRegistry.getConnectedOrErrorState();
        if (state && !state.poppedOut && !state.minimized) {
          this.deviceStateRegistry.minimizeToggle(state.device, true);
        }

        // Preview attachment or any other ticket-related work-panels should be toggled
        // only from the main window
        if (!this.popOut) {
          this.attachmentService.clearPreviewAttachment();
        }
        // end
      }
    );

    this.openLiveSnapshotSub = this.globalEventBusService.register(
      GlobalEvents.openLiveSnapshot,
      (result: ISnapshotInformation) => {
        const device = result ? result.device : null;
        this.workPanelKey = result.workPanelKey;

        // workPanelKey is undefined means we are opening a new liveSnapshot and if key is not undefined then it is already open
        const closePopOutWindow = !result.workPanelKey;
        this.addWorkPanel(WorkPanelType.liveSnapshot, device, null, closePopOutWindow);

        // Preview attachment or any other ticket-related work-panels should be toggled
        // only from the main window
        if (!this.popOut) {
          this.attachmentService.clearPreviewAttachment();
        }
        this.setLicenseActive(result.device.mcConnectionId);
      }
    );

    this.openRcWorkPanelSub = this.globalEventBusService.register(
      GlobalEvents.openRcWorkPanel,
      (result: IWorkPanelInformation) => {
        if (result.workPanelKey) {
          this.workPanelKey = result.workPanelKey;
        }

        // workPanelKey is undefined means we are opening a new Rc and if key is not undefined then it is already open
        const closePopOutWindow = !result.workPanelKey;
        this.addWorkPanel(WorkPanelType.rc, result.device, null, closePopOutWindow);
        this.setLicenseActive(result.device.mcConnectionId);
      }
    );

    this.remoteControlService.remoteControlStarted$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((device: IDeviceModel) => {
        // We only need to add RC workpanel from this event listener when we are in either standalone or popout modes.
        // During popin it is added from a different place.
        // Also, there is an scenario were if we do this from popin, we will override the real forgound panel (HD-13147)
        if (this.standalone || this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.rc)) {
          this.addWorkPanel(WorkPanelType.rc, device, null, true);
        }
      });

    this.remoteControlService.remoteControlEnded$.pipe(takeUntil(this.unsubscribe$)).subscribe((deviceId: string) => {
      this.isLicenseActive = null;
      if (this.minimizedWorkPanelKey) {
        const device = this.workPanelManagerService.getPanelDevice(this.minimizedWorkPanelKey);
        if (device !== null && device.deviceId === deviceId) {
          this.workPanelManagerService.removeWorkPanel(this.minimizedWorkPanelKey);
          this.minimizedWorkPanelKey = null;
        }
      }
    });

    this.remoteControlCloseInitiatedSub = this.globalEventBusService.register(
      GlobalEvents.remoteControlCloseInitiated,
      this.remoteControlCloseInitiated
    );

    this.remoteControlMaximizedSub = this.globalEventBusService.register(GlobalEvents.remoteControlMaximized, () => {
      this.transferMinimizedPanelToEmbedded();
      const panelType = this.workPanelManagerService.getPanelType(this.workPanelKey);
      if (WorkPanelType.liveSnapshot === panelType) {
        const device = this.workPanelManagerService.getPanelDevice(this.workPanelKey);
        const snapshotInfo: ISnapshotInformation = {
          device: device,
          mcConnectionName: device.$$mcConnectionName,
          snapshotActivity: null,
          workPanelKey: null,
        };
        this.globalEventBusService.broadcast(GlobalEvents.openLiveSnapshot, snapshotInfo);
      }
      this.attachmentService.clearPreviewAttachment();
    });

    this.remoteControlMinimizedSub = this.globalEventBusService.register(GlobalEvents.remoteControlMinimized, () => {
      if (this.isFilePreviewOrSnapshotPreview()) {
        // we dont close workpanel because preview snapshot or file preview is active, we also don't call transferEmbeddedPanelToMinimized because it's called already
        this.openWorkPanelEvent.emit();
      } else {
        // currently no active panel so we close it
        this.transferEmbeddedPanelToMinimized();
        this.closeWorkPanelEvent.emit();
      }
    });
    this.popOutStartedSub = this.globalEventBusService.register(
      GlobalEvents.popOutStarted,
      (workPanelType: WorkPanelType) => {
        // if we have minimized WorkPanel and embedded we should popOut correct key.

        const panelType = this.workPanelManagerService.getPanelType(this.workPanelKey);
        if (panelType === workPanelType) {
          this.workPanelService.popOut(this.workPanelKey);
          return;
        }

        const minimizedPanelType = this.workPanelManagerService.getPanelType(this.minimizedWorkPanelKey);
        if (minimizedPanelType === workPanelType) {
          this.workPanelService.popOut(this.minimizedWorkPanelKey);
          this.minimizedWorkPanelKey = null;
        }
      }
    );

    this.popInStartedSub = this.globalEventBusService.register(
      GlobalEvents.popInStarted,
      (workPanelType: WorkPanelType) => {
        this.workPanelService.popIn();
      }
    );

    this.showToastMessageSub = this.globalEventBusService.register(GlobalEvents.showToastMessage, (toast: Toast) => {
      this.setToast(toast);
    });

    this.globalEventBusService.register(GlobalEvents.deviceMenuErrorMessage, this.onErrorHandler);

    this.globalEventBusService.register(GlobalEvents.remoteControlMultiScreenVisualSelection, (inVisual: boolean) => {
      this._isInVisualSelectionMode = inVisual;
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    if (this.previewSnapshotSub) {
      this.previewSnapshotSub.unsubscribe();
    }

    if (this.openRcWorkPanelSub) {
      this.openRcWorkPanelSub.unsubscribe();
    }

    if (this.rcMaximizedSub) {
      this.rcMaximizedSub.unsubscribe();
    }

    if (this.openLiveSnapshotSub) {
      this.openLiveSnapshotSub.unsubscribe();
    }

    if (this.remoteControlMaximizedSub) {
      this.remoteControlMaximizedSub.unsubscribe();
    }

    if (this.remoteControlMinimizedSub) {
      this.remoteControlMinimizedSub.unsubscribe();
    }

    if (this.popOutStartedSub) {
      this.popOutStartedSub.unsubscribe();
    }

    if (this.popInStartedSub) {
      this.popInStartedSub.unsubscribe();
    }

    if (this.remoteControlCloseInitiatedSub) {
      this.remoteControlCloseInitiatedSub.unsubscribe();
    }

    if (this.externalDisableSubscription) {
      this.externalDisableSubscription.unsubscribe();
    }

    if (this.showToastMessageSub) {
      this.showToastMessageSub.unsubscribe();
    }

    if (this.attachmentPreviewUnbind) {
      this.attachmentPreviewUnbind.unsubscribe();
    }

    if (this.remoteControlMinimizedUnbind) {
      this.remoteControlMinimizedUnbind();
    }

    this.workPanelManagerService.clear();
  }

  public getGroups = () => {
    return this.groups;
  };

  public isPopInOutIsDisabled = (): boolean => {
    return this.isPopInOutExternallyDisabled || this.isActiveRecording();
  };

  public isFilePreviewVisible = (): boolean => {
    return (
      this.isCurrentPanelType(WorkPanelType.filePreview) &&
      // HD-11194 temporary fix only for 1.8, but this might need to stay, further investigation needed
      // start
      !!this.attachmentService.getPreviewAttachment()
    );
    // end
  };

  public isSnapshotVisible = (): boolean => {
    return this.isPanelVisible(WorkPanelType.liveSnapshot) || this.isPanelVisible(WorkPanelType.savedSnapshot);
  };

  public hasTitle = (): boolean => {
    return this.isRcPanelVisible() || this.isSnapshotVisible();
  };

  public isRcPanelVisible = (): boolean => {
    return this.isPanelVisible(WorkPanelType.rc);
  };

  public isAttachmentEnabled = () => {
    const isAllowed = this.popOut && this.canAddAttachment;
    if (this.standalone) {
      return isAllowed;
    }

    return this.ticketContextService.canAddAttachment() || isAllowed;
  };

  /** when the x button is clicked */
  public closeWorkPanel = (workPanelType: number) => {
    let device: IDeviceModel;
    if (
      this.workPanelManagerService.isPanelTypeEmbedded(workPanelType) &&
      (workPanelType === WorkPanelType.savedSnapshot || workPanelType === WorkPanelType.filePreview)
    ) {
      device = this.workPanelManagerService.getPanelDevice(this.minimizedWorkPanelKey);
      if (device) {
        this.snapshotService.clearElementInViewState(device.deviceId);
      }

      this.workPanelManagerService.removeActiveWorkPanelsByStatus(WorkPanelStatus.embedded);
      const state = device ? this.deviceStateRegistry.getState(device.deviceId) : null;
      if (state && (state.connected || state.hasError) && state.minimized) {
        this.deviceStateRegistry.minimizeToggle(state.device, false);
      } else {
        this.closeWorkPanelEvent.emit();
      }
    }

    if (workPanelType === WorkPanelType.liveSnapshot) {
      device = this.workPanelManagerService.getPanelDevice(this.workPanelKey);
      if (device) {
        this.snapshotService.clearElementInViewState(device.deviceId);
      }

      const deviceRcState = this.deviceStateRegistry.getState(device.deviceId);
      if (deviceRcState) {
        this.remoteControlService.stopRemoteControl(device as hd.IDeviceModel);
      }

      this.workPanelManagerService.removeWorkPanel(this.workPanelKey);
      this.closeWorkPanelEvent.emit();
    }
  };

  public addWorkPanel = (
    panelType: number,
    device?: IDeviceModel,
    customProperty?: { [key: string]: any },
    closePopOutWindow?: boolean
  ) => {
    if ((panelType === WorkPanelType.liveSnapshot || panelType === WorkPanelType.rc) && !device) {
      // rc or live snapshot panel must have a device associate to it
      console.error('Adding work panel failed: rc work or live snapshot panel must have a device associate to it');
      return;
    }

    if (closePopOutWindow && this.isRcOrLiveSnapshotPoppedOut()) {
      this.workPanelService.closePopOutWindow();
    }

    if (
      !this.workPanelKey ||
      this.workPanelManagerService.getPanelType(this.workPanelKey) !== panelType ||
      (this.workPanelManagerService.getPanelDevice(this.workPanelKey) &&
        device &&
        (this.workPanelManagerService.getPanelDevice(this.workPanelKey).deviceId !== device.deviceId ||
          this.workPanelManagerService.getPanelDevice(this.workPanelKey).isAgentOnline !== device.isAgentOnline))
    ) {
      let workPanelStatus: number;
      if (this.standalone) {
        workPanelStatus = WorkPanelStatus.standalone;
      } else if (this.popOut) {
        workPanelStatus = WorkPanelStatus.poppedOut;
      } else {
        workPanelStatus = WorkPanelStatus.embedded;
      }

      // Before adding a new work panel, need to unbind the old panel to avoid not necessary updating (leads to update component after "detach" (HD-14213))
      this.resetTitleBar();
      this.resetActionBar();

      this.workPanelKey = this.workPanelManagerService.addWorkPanel(panelType, workPanelStatus, device, customProperty);
    }

    if (this.workPanelKey) {
      if (this.workPanelManagerService.canPanelBePoppedOut(panelType)) {
        this.workPanelService.workPanelKey = this.workPanelKey;
      }

      this.openWorkPanelEvent.emit();
    } else {
      console.error('Adding work panel failed');
    }
  };

  public minimizeWorkPanelInitiated = () => {
    const rcState = this.deviceStateRegistry.getCurrentState(); // we can only mimimize the panel when rc is connected

    if (rcState && rcState.device) {
      this.deviceStateRegistry.minimizeToggle(rcState.device);
    }
  };

  public isMinimizeButtonVisible = (): boolean => {
    return (
      this.deviceStateRegistry.anyRcConnected() && this.isRcOrLiveSnapshotVisible() && !this.popOut && !this.standalone
    );
  };

  public isSavedSnapshotVisible = (): boolean => {
    return this.isPanelVisible(WorkPanelType.savedSnapshot);
  };

  public isRcOrLiveSnapshotVisible = (): boolean => {
    return this.isPanelVisible(WorkPanelType.liveSnapshot) || this.isPanelVisible(WorkPanelType.rc);
  };

  public isRcOrLiveSnapshotMinimized = (): boolean => {
    return this.isPanelMinimized(WorkPanelType.liveSnapshot) || this.isPanelMinimized(WorkPanelType.rc);
  };

  public isRcOrLiveSnapshotPoppedOut = (): boolean => {
    return (
      this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.rc) ||
      this.workPanelManagerService.isPanelTypePoppedOut(WorkPanelType.liveSnapshot)
    );
  };

  public isSplashScreenVisible = (): boolean => {
    return (
      !this.popOut &&
      !this.standalone &&
      !this.isRcOrLiveSnapshotVisible() &&
      !this.isSavedSnapshotVisible() &&
      !this.isPanelVisible(WorkPanelType.filePreview)
    );
  };

  public isMenuBarCollapsible = (): boolean => {
    if (!this.menuBarItems) {
      return false;
    }

    const filtered = this.menuBarItems.filter(i => i.isVisible && i.isVisible());
    return filtered.length > this.maxMenuBarItemNotCollapsible;
  };

  public setTitleBar = (titleBarRef: TemplateRef<any>, workPanelType: WorkPanelType) => {
    if (this.isCurrentPanelType(workPanelType)) {
      this.titleBarRef = titleBarRef;
    }
  };

  public setSnapshotTitleBar = (titleBarRef: TemplateRef<any>) => {
    const workPanelType: WorkPanelType = this.workPanelManagerService.getPanelType(this.workPanelKey);

    if (workPanelType == WorkPanelType.liveSnapshot || workPanelType == WorkPanelType.savedSnapshot) {
      this.titleBarRef = titleBarRef;
    }
  };

  public setActionBar = (actionBarRef: TemplateRef<any>, workPanelType: WorkPanelType) => {
    if (this.isCurrentPanelType(workPanelType)) {
      this.actionBarRef = actionBarRef;
    }
  };

  public setToast = (toast: Toast) => {
    this.toast = toast;
    // this.rootScope.$digest();
  };

  public updateRcMenuBarItems = (items: WorkPanelBarItem[]) => {
    this.rcMenuBarItems = items;
    this.updateMenuBarItems();
  };

  public updateSnapshotMenuBarItems = (items: WorkPanelBarItem[]) => {
    this.snapshotMenuBarItems = items;
    this.updateMenuBarItems();
  };

  /** combinding menu bars
   *  the order should be:
   *  1. RC
   *  2. Snapshot
   *  3. ( the rest, according to device type, remote-control-view will handle show and hiding those buttons)
   */
  public updateMenuBarItems = () => {
    if (this.rcMenuBarItems && this.snapshotMenuBarItems) {
      this.menuBarItems = [...this.rcMenuBarItems];

      this.menuBarItems.splice(1, 0, this.snapshotMenuBarItems[0]);
    }
  };

  public getCurrentRcState = (): RemoteControlDeviceState => {
    const currentDevice = this.workPanelManagerService.getPanelDevice(this.workPanelKey);
    if (currentDevice && currentDevice.deviceId) {
      const state = this.deviceStateRegistry.getState(currentDevice.deviceId);

      // device state without device is not fully. Just wait for the device to be assigned.
      return state && state.device ? state : null;
    }
    return null;
  };

  private _isLicenseActive = (): boolean => {
    return this.licenseInfoService && this.licenseInfoService.isLicenseActive();
  };

  public onSnapshotRefreshInProgressChanged(inProgress: boolean) {
    this.snapshotRefreshInProgress = inProgress;
  }

  private isMemberOfCreatingGroup = () => {
    const groupMemberships = this.authorizationService.getGroups();

    let result = this.authorizationService.isSystemAdmin();
    result = result || groupMemberships.length > 0;
    return result;
  };

  private isRcConnectedOrHasError = (): boolean => {
    const rcState = this.getCurrentRcState();
    if (!rcState) {
      return false;
    }
    return rcState.connected || rcState.hasError;
  };

  private isCurrentPanelType = (panel: WorkPanelType): boolean => {
    return this.workPanelManagerService.getPanelType(this.workPanelKey) === panel;
  };

  private isFilePreviewOrSnapshotPreview = (): boolean => {
    return (
      this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.filePreview) ||
      this.workPanelManagerService.isPanelTypeEmbedded(WorkPanelType.savedSnapshot)
    );
  };

  private transferEmbeddedPanelToMinimized = () => {
    if (this.workPanelKey) {
      this.minimizedWorkPanelKey = this.workPanelManagerService.changePanelStatus(
        this.workPanelKey,
        WorkPanelStatus.minimized
      );
      this.workPanelKey = null;
    } else {
      console.warn('work panel key not found in transferEmbeddedPanelToMinimized');
    }
  };

  private transferMinimizedPanelToEmbedded = () => {
    if (this.minimizedWorkPanelKey) {
      this.workPanelKey = this.workPanelManagerService.changePanelStatus(
        this.minimizedWorkPanelKey,
        WorkPanelStatus.embedded
      );
      this.minimizedWorkPanelKey = null;
      this.openWorkPanelEvent.emit();
    } else {
      console.warn('minimized workpanel key not found in transfeMinimizedPanelToEmbedded');
    }
  };

  private remoteControlCloseInitiated = () => {
    if (this.isRcOrLiveSnapshotVisible()) {
      const device = this.workPanelManagerService.getPanelDevice(this.workPanelKey);
      if (device) {
        this.snapshotService.clearElementInViewState(device.deviceId);
      }

      this.workPanelManagerService.removeWorkPanel(this.workPanelKey);
      this.workPanelKey = null;

      if (!this.minimizedWorkPanelKey) {
        this.closeWorkPanelEvent.emit();
        this.errorHandlerService.clearError();
      }
    } else if (this.isRcOrLiveSnapshotMinimized()) {
      const device = this.workPanelManagerService.getPanelDevice(this.minimizedWorkPanelKey);
      if (device) {
        this.snapshotService.clearElementInViewState(device.deviceId);
      }

      this.workPanelManagerService.removeWorkPanel(this.minimizedWorkPanelKey);
      this.minimizedWorkPanelKey = null;

      if (!this.workPanelKey) {
        this.closeWorkPanelEvent.emit();
        this.errorHandlerService.clearError();
      }
    } else if (this.isFilePreviewOrSnapshotPreview() && this.minimizedWorkPanelKey) {
      const device = this.workPanelManagerService.getPanelDevice(this.minimizedWorkPanelKey);
      if (device) {
        this.snapshotService.clearElementInViewState(device.deviceId);
      }

      this.workPanelManagerService.removeWorkPanel(this.minimizedWorkPanelKey);
      this.minimizedWorkPanelKey = null;
    }

    if (this.isRcOrLiveSnapshotPoppedOut()) {
      this.workPanelService.closePopOutWindow();
    }
  };

  public isPanelMinimized = (panelType: WorkPanelType) => {
    return (
      !this.popOut &&
      this.workPanelManagerService.getPanelType(this.minimizedWorkPanelKey) === panelType &&
      this.workPanelManagerService.isPanelTypeMinimized(panelType)
    );
  };

  public isPanelVisible = (panelType: WorkPanelType): boolean => {
    // let panelStatus = this.standalone
    //   ? WorkPanelStatus.standalone
    //   : this.popOut
    //   ? WorkPanelStatus.poppedOut
    //   : WorkPanelStatus.embedded;

    return this.isCurrentPanelType(panelType);
  };

  public canCreateTicket = (): boolean => {
    return this.standalone && this.isLicenseActive && this.isMemberOfCreatingGroup() && !this.rcHasError();
  };

  public rcHasError = (): boolean => {
    const deviceState = this.getCurrentRcState();
    return deviceState && deviceState.hasError;
  };

  public createAccess = (): boolean => {
    return this.authorizationService.hasCreateAccess();
  };

  public associateWithTicket = (ticket?: hd.ITicketModel) => {
    // if (!this._alreadyAssociating) {
    // Get the device Id from the currently available workpanel
    // (NOTE: When in standalone, all panels refer to the same device)

    if (!this.workPanelKey) {
      return;
    }
    const devicePanel = this.workPanelManagerService.getPanelDevice(this.workPanelKey);
    let deviceId;
    if (!!devicePanel) {
      deviceId = devicePanel.deviceId;
    }

    if (!deviceId) {
      console.warn('No device to associate with ticket');
      return;
    }

    this.userInfoService.getCurrentUser().subscribe(user => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (ticket as any).watchers = [user];

      ticket.reporterPublicId = this.authorizationService.getUserId();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (ticket as any).devices = [this.workPanelManagerService.getPanelDevice(this.workPanelKey)];

      this._alreadyAssociating = true;
      let observable: Observable<hd.ITicketModel>;

      // Associate to a new or existing ticket
      this.groupContextService.currentGroupId = ticket.groupId;
      if (ticket && ticket.ticketId !== undefined) {
        observable = this.ticketService.associateWithExistingTicket(deviceId, ticket);
      } else {
        observable = this.ticketService.associateWithNewTicket(deviceId, ticket);
      }

      // On success, redirect to view ticket page, otherwise set error
      observable.subscribe(
        (ticketModel: hd.ITicketModel) => {
          this.errorHandlerService.clearError();

          this.workPanelService.embedIntoTicket(this.workPanelKey);
          this.$uiRouter.navigate(['ticket', ticketModel.ticketId]);
          this._alreadyAssociating = false;
        },
        (error: hd.IError) => {
          console.error(error);
          this.errorHandlerService.setError(error);
          this._alreadyAssociating = false;
        }
      );
    });
  };

  private associateWithTicketIfNeeded = () => {
    if (this.standalone && !this._alreadyAssociating) {
      if (this.isLicenseActive) {
        const remoteControlConfigResource = this.appSettingsService.getRcConfiguration();
        if (remoteControlConfigResource) {
          remoteControlConfigResource.subscribe(
            (config: IRcConfiguration) => {
              this.errorHandlerService.clearError();
              if (config && config.autoCreationTicketEnabled) {
                if (this.canEmbedIntoTicket) {
                  let ticketTemplate = <hd.ITicketModel>{};
                  this.groupContextService.getAllowedGroups().then((groups: IGroupModel[]) => {
                    if (groups.length > 1) {
                      this.showTicketCreate = true;
                    } else if (groups.length === 1) {
                      ticketTemplate.groupId = groups[0].groupId;
                      this.associateWithTicket(ticketTemplate);
                    }
                  });
                } else {
                  this._alreadyAssociating = false;
                }
              } else {
                this._alreadyAssociating = false;
              }
            },
            (err: hd.IError) => {
              if (err.status === 403) {
                err.isHandled = true;
                console.error('Unauthorized auto create ticket is disabled');
              }

              console.error(err);
              this.errorHandlerService.setError(err);
            }
          );
        }
      } else {
        console.log('Ticket auto-creation failed: no permission to create ticket or no active licence');
      }
    }
  };

  public onGroupSelected = (groupId: number) => {
    if (groupId !== undefined) {
      let ticketTemplate = <hd.ITicketModel>{};
      ticketTemplate.groupId = groupId;

      this.associateWithTicket(ticketTemplate);
    }

    this.showTicketCreate = false;
  };

  public isActiveRecording = () => {
    const deviceState = this.getCurrentRcState();

    if (!deviceState || !deviceState.device) {
      return false;
    }

    // Embed should not be allowed while accept agent-side video recording is still pending
    // For Android Agent ver. 14.0 and bellow
    return (
      this.deviceStateRegistry.isRecording(deviceState.device) || this.deviceStateRegistry.isRecordingPendingAccept()
    );
  };

  private resetActionBar = () => {
    this.actionBarRef = null;
  };

  private resetTitleBar = () => {
    this.titleBarRef = null;
  };

  public get getError() {
    return this.errorMessage;
  }

  public onErrorHandler = (param: string) => {
    this.errorMessage = param;
  };

  public get isRcRunning(): boolean {
    return this.deviceStateRegistry.isCurrentRemoteControlRunning();
  }

  public get isInVisualSelectionMode(): boolean {
    return this._isInVisualSelectionMode;
  }
}
